import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveAssetUrl } from "@/lib/api";
import { mainSliderDetailState, MAIN_SLIDER_RETURN } from "@/lib/mainSliderNavigation";
import { heroSlides as fallbackSlides } from "@/mocks/bioconData";
import type { HeroSlide } from "@/lib/types";

type SlideView = {
  id: number;
  title: string;
  date: string;
  imageUrl: string | null;
  link: string | null;
};

function toSlideView(slide: HeroSlide): SlideView {
  return {
    id: slide.id,
    title: slide.title,
    date: slide.date,
    imageUrl: slide.imageUrl,
    link: slide.link,
  };
}

function fallbackToSlideViews(): SlideView[] {
  return fallbackSlides.map((s) => ({
    id: s.id,
    title: s.title,
    date: s.date,
    imageUrl: s.image,
    link: s.link,
  }));
}

const titleLinkClass =
  "text-white text-xl md:text-2xl lg:text-3xl font-light leading-snug hover:underline block max-w-4xl cursor-pointer";

function SlideTitle({ slide }: { slide: SlideView }) {
  const externalLink = slide.link?.trim();
  if (externalLink) {
    const isExternal = externalLink.startsWith("http://") || externalLink.startsWith("https://");
    if (isExternal) {
      return (
        <a href={externalLink} rel="nofollow noreferrer" target="_blank" className={titleLinkClass}>
          {slide.title}
        </a>
      );
    }
    return (
      <Link to={externalLink} className={titleLinkClass}>
        {slide.title}
      </Link>
    );
  }
  return (
    <Link
      to={`/main-slider/${slide.id}`}
      state={mainSliderDetailState(MAIN_SLIDER_RETURN.home)}
      className={titleLinkClass}
    >
      {slide.title}
    </Link>
  );
}

export default function HeroSlider() {
  const [slides, setSlides] = useState<SlideView[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  const fetchSlides = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi.get<HeroSlide[]>("/public/main-slider");
      const active = (res.data || []).filter((s) => s.status === "active");
      if (active.length > 0) {
        setSlides(active.map(toSlideView));
      } else {
        setSlides(fallbackToSlideViews());
      }
      setCurrent(0);
    } catch {
      setSlides(fallbackToSlideViews());
      setCurrent(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  const next = useCallback(() => {
    setCurrent((prev) => (slides.length > 0 ? (prev + 1) % slides.length : 0));
  }, [slides.length]);

  const prev = () => {
    setCurrent((prev) => (slides.length > 0 ? (prev - 1 + slides.length) % slides.length : 0));
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  if (loading || slides.length === 0) {
    return null;
  }

  const showControls = slides.length > 1;

  return (
    <section className="relative w-full overflow-hidden pt-[96px]">
      <div className="relative w-full h-[420px] md:h-[520px] lg:h-[600px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === current ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <img
              src={resolveAssetUrl(slide.imageUrl)}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-black/20" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="container mx-auto">
                <p className="text-white/90 text-sm md:text-base mb-2">{slide.date}</p>
                <SlideTitle slide={slide} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {showControls && (
        <>
          <div className="absolute bottom-4 right-4 md:right-10 z-20 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                  i === current ? "bg-white w-6" : "bg-white/40 hover:bg-white/70"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <button
            onClick={prev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer backdrop-blur-sm"
            aria-label="Previous slide"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full border border-white/40 bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer backdrop-blur-sm"
            aria-label="Next slide"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </>
      )}
    </section>
  );
}
